import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BreadcrumbService } from 'xng-breadcrumb';
import { FormService } from '../../../../../../shared/store/form/form.service';
import { SubHeaderService } from '../../../../../../shared/store/subheader/subheader.service';
import {
  DataType,
  DropdownStyle,
} from '../../../../../../shared/store/typeahead/types';
import { View } from '../../../../../../shared/store/view/types';
import { ViewService } from '../../../../../../shared/store/view/views.service';
import { WorkLocationService } from '../../../../../../shared/store/attendance/work-location/work-location.service';
import {
  WorkLocationModel,
  WorkLocationType,
} from '../../../../../../shared/store/attendance/work-location/types';

@UntilDestroy()
@Component({
  selector: 'app-work-location-form-content',
  standalone: false,
  templateUrl: './work-location-form-content.component.html',
  styleUrl: './work-location-form-content.component.scss',
})
export class WorkLocationFormContentComponent implements OnInit, OnDestroy {
  view$: Observable<View>;
  workLocation$: Observable<WorkLocationModel>;
  workLocationInstance$: Observable<WorkLocationModel>;

  workLocationForm: FormGroup;

  isEditMode = false;
  workLocationId: number;

  protected readonly DataType = DataType;
  protected readonly DropdownStyle = DropdownStyle;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly workLocationService: WorkLocationService,
    private readonly subHeaderService: SubHeaderService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly viewService: ViewService,
    private readonly formService: FormService,
  ) {
    this.view$ = this.viewService.getViews.value$;
    this.workLocation$ = this.workLocationService.getById.value$;
    this.workLocationInstance$ = this.workLocationService.createInstance.value$;
  }

  ngOnInit(): void {
    this.workLocationForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      type: [null, [Validators.required]],
      salesTeamId: [null],
      countedAsWork: [true],
      active: [true],
    });

    this.workLocationForm.controls['type'].valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((type: WorkLocationType) =>
        this.updateSalesTeamValidators(type),
      );

    this.workLocationId = this.route.snapshot.params['id'];
    this.isEditMode = !!this.workLocationId;

    if (this.isEditMode) {
      this.workLocationService.getById.call(this.workLocationId);
    } else {
      this.workLocationService.createInstance.call();
    }

    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'workLocationCreate' || x == 'workLocationUpdate')
          this.onSubmit();
        if (x == 'workLocationList') {
          this.formService.clear();
          this.router.navigate(['/Configuration/WorkLocations/List']);
        }
        if (x == 'workLocationDetails') {
          this.formService.clear();
          this.router.navigate([
            '/Configuration/WorkLocations/Details',
            this.workLocationId,
          ]);
        }
        this.subHeaderService.setTriggerAction('');
      });

    this.view$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@list', x.title);
        if (!this.isEditMode) {
          this.breadcrumbService.set('@workLocationCreate', x.breadcrumbCreate);
        }
      });

    this.workLocation$
      .pipe(
        untilDestroyed(this),
        filter(() => this.isEditMode),
        filter((x) => !!x),
        take(1),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@workLocationEdit', x.name);
        this.patchForm(x);
      });

    this.workLocationInstance$
      .pipe(
        untilDestroyed(this),
        filter(() => !this.isEditMode),
        filter((x) => !!x),
        take(1),
      )
      .subscribe((x) => this.patchForm(x));
  }

  private updateSalesTeamValidators(type: WorkLocationType) {
    const salesTeamId = this.workLocationForm.controls['salesTeamId'];
    if (type === 'Office') {
      salesTeamId.setValidators(Validators.required);
    } else {
      salesTeamId.clearValidators();
      salesTeamId.setValue(null);
    }
    salesTeamId.updateValueAndValidity({ emitEvent: false });
  }

  private patchForm(x: WorkLocationModel) {
    this.workLocationForm.patchValue({
      name: x.name,
      type: x.type,
      salesTeamId: x.salesTeamId == 0 ? null : x.salesTeamId,
      countedAsWork: x.countedAsWork,
      active: x.active,
    });
  }

  onSubmit() {
    if (this.workLocationForm.invalid) {
      this.workLocationForm.markAllAsTouched();
      this.formService.setEntityErrors('WORK_LOCATION');
      this.formService.countErrors(this.workLocationForm, true);
      return;
    }

    const workLocationModel: WorkLocationModel = {
      id: this.isEditMode ? +this.workLocationId : 0,
      name: this.workLocationForm.value.name,
      type: this.workLocationForm.value.type,
      salesTeamId: this.workLocationForm.value.salesTeamId ?? null,
      countedAsWork: this.workLocationForm.value.countedAsWork,
      active: this.workLocationForm.value.active,

      //Others
      typeLoc: '',
      salesTeamName: '',
    };

    const result$ = this.isEditMode
      ? this.workLocationService.update.call(workLocationModel)
      : this.workLocationService.add.call(workLocationModel);

    result$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
        take(1),
      )
      .subscribe((result) => {
        this.formService.clear();
        this.router.navigate([
          '/Configuration/WorkLocations/Details',
          result.id,
        ]);
      });
  }

  ngOnDestroy(): void {
    if (this.isEditMode) {
      this.workLocationService.getById.reset();
      this.workLocationService.update.reset();
      this.breadcrumbService.set('@workLocationEdit', ' ');
    } else {
      this.workLocationService.add.reset();
      this.workLocationService.createInstance.reset();
      this.breadcrumbService.set('@workLocationCreate', ' ');
    }
  }
}
